// src/client/super/Messages.tsx
import React, { useEffect, useMemo, useState } from "react";
import { api, type HistoryItem, type SendMessagePayload, type StaffRole } from "../lib/api";
import { can, normalizeRole, type Role } from "../lib/roles";
import { ROUTE } from "../lib/paths";

/** Пункты выбора ролей (чекбоксы-«пилюли») */
const ROLE_OPTIONS: { key: StaffRole; label: string }[] = [
  { key: "owner",      label: "Владельцы" },
  { key: "superadmin", label: "Суперадмины" },
  { key: "admin",      label: "Админы" },
  { key: "door",       label: "Фейс-контроль" },
];

const STAGE_OPTIONS: { key: 'eula'|'profile'|'payment'|'pending'|'pass'|'error'|'done'; label: string }[] = [
  { key: 'eula',    label: 'Оферта' },
  { key: 'profile', label: 'Профиль' },
  { key: 'payment', label: 'Оплата' },
  { key: 'pending', label: 'Ожидание' },
  { key: 'pass',    label: 'Пропуск' },
  { key: 'error',   label: 'Ошибка' },
  { key: 'done',    label: 'Завершил' },
];

export default function Messages() {
  const [role, setRole] = useState<Role>(null);

  // выбранные роли для рассылки (по умолчанию — админы и фейсконтроль)
  const [roles, setRoles] = useState<StaffRole[]>(["admin", "door"]);
  const [text, setText] = useState("");
  const [disablePreview, setDisablePreview] = useState(true);
  const [btnText, setBtnText] = useState("");
  const [btnUrl, setBtnUrl] = useState("");
  const [testId, setTestId] = useState("");
  const [sending, setSending] = useState(false);

  const [history, setHistory] = useState<HistoryItem[]>([]);

  const [audience, setAudience] = useState<'staff'|'guests'>('staff');
  const [stages, setStages] = useState<Array<'eula'|'profile'|'payment'|'pending'|'pass'|'error'|'done'>>(['payment','pending']);
  const [eventId, setEventId] = useState<string>('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const me = await api.me();
        if (!alive) return;
        setRole(normalizeRole(me.role));
      } catch {
        setRole(null);
      }
      try {
        const h = await api.messages.history();
        if (!alive) return;
        setHistory(h);
      } catch {}
    })();
    return () => { alive = false; };
  }, []);

  const canSend = can(role, "messages");

  function toggleRole(r: StaffRole) {
    setRoles((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
  }

  function toggleStage(s: (typeof STAGE_OPTIONS)[number]['key']) {
    setStages((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  }

  const payload: any = useMemo(() => {
    if (!text.trim()) return null;
    if (audience === 'staff') {
      if (!roles.length) return null;
      return {
        audience: 'staff',
        roles: roles.slice(),
        text: text.trim(),
        disable_preview: disablePreview,
        button_text: btnText.trim() || null,
        button_url: btnUrl.trim() || null,
        test_tg_id: testId.trim() ? Number(testId.trim()) : null,
      };
    } else {
      if (!stages.length) return null;
      return {
        audience: 'guests',
        stages: stages.slice(),
        event_id: eventId.trim() ? Number(eventId.trim()) : null,
        text: text.trim(),
        disable_preview: disablePreview,
        button_text: btnText.trim() || null,
        button_url: btnUrl.trim() || null,
        test_tg_id: testId.trim() ? Number(testId.trim()) : null,
      };
    }
  }, [audience, roles, stages, eventId, text, disablePreview, btnText, btnUrl, testId]);

  async function onSend() {
    if (!payload || !canSend || sending) return;
    setSending(true);
    try {
      const r = await api.messages.send(payload);
      alert(
        payload.test_tg_id
          ? `Тестовое сообщение отправлено (tg_id=${payload.test_tg_id}).`
          : `Отправлено: ${r.sent ?? 0} / ${r.total ?? 0}${r.failed ? `, ошибок: ${r.failed}` : ""}`
      );
      const h = await api.messages.history();
      setHistory(h);
    } catch (e: any) {
      alert(e?.message || "Не удалось отправить");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="space-y-6">
      <section className="ui-card p-4 sm:p-5">
        <header className="mb-4 flex items-center justify-between gap-3">
          <h1 className="flex-1 text-[32px] leading-[1.05] font-extrabold tracking-tight">
            Рассылки
          </h1>

          <button
            onClick={() => ROUTE.go(ROUTE.super())}
            className="shrink-0 whitespace-nowrap rounded-xl border border-white/15 bg-white/10 px-4 py-2
                      text-sm text-white hover:bg-white/15"
          >
            На главную
          </button>
        </header>

        <div className="mb-4 flex gap-2">
          <button
            onClick={() => setAudience('staff')}
            className={[
              'rounded-xl px-3 py-1.5 text-sm border',
              audience==='staff' ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-100' : 'border-white/12 bg-white/10 text-white/90 hover:bg-white/15',
            ].join(' ')}
          >Персонал</button>
          <button
            onClick={() => setAudience('guests')}
            className={[
              'rounded-xl px-3 py-1.5 text-sm border',
              audience==='guests' ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-100' : 'border-white/12 bg-white/10 text-white/90 hover:bg-white/15',
            ].join(' ')}
          >Гости</button>
        </div>

        {!canSend ? (
          <div className="ui-panel text-sm text-neutral-300">
            У вашей роли нет прав на рассылки. Обратитесь к супер-админу.
          </div>
        ) : (
          <>
            {audience==='staff' ? (
              <>
                <div className="mb-3 text-sm text-neutral-300">Получатели (роли)</div>
                <div className="mb-4 grid grid-cols-2 gap-2">
                  {ROLE_OPTIONS.map(({ key, label }) => {
                    const checked = roles.includes(key);
                    return (
                      <label key={key} className={[ 'flex w-full items-center gap-2 rounded-2xl border px-3 py-2', checked ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-100' : 'border-white/12 bg-white/10 text-white/90 hover:bg-white/15', ].join(' ')}>
                        <input type="checkbox" className="h-4 w-4 accent-emerald-500" checked={checked} onChange={() => toggleRole(key)} />
                        <span className="truncate text-sm leading-tight">{label}</span>
                      </label>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                <div className="mb-3 text-sm text-neutral-300">Получатели (этапы гостей)</div>
                <div className="mb-4 grid grid-cols-2 gap-2">
                  {STAGE_OPTIONS.map(({ key, label }) => {
                    const checked = stages.includes(key);
                    return (
                      <label key={key} className={[ 'flex w-full items-center gap-2 rounded-2xl border px-3 py-2', checked ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-100' : 'border-white/12 bg-white/10 text-white/90 hover:bg-white/15', ].join(' ')}>
                        <input type="checkbox" className="h-4 w-4 accent-emerald-500" checked={checked} onChange={() => toggleStage(key)} />
                        <span className="truncate text-sm leading-tight">{label}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="mb-4 flex items-center gap-2">
                  <input
                    className="ui-input w-[180px]"
                    placeholder="ID события (опц.)"
                    inputMode="numeric"
                    value={eventId}
                    onChange={(e) => setEventId(e.target.value.replace(/[^\d]/g, ''))}
                  />
                  <span className="text-xs text-neutral-400">если указан — фильтруем по событию</span>
                </div>
              </>
            )}

            <label className="mb-3 block text-sm text-neutral-300">Текст сообщения</label>
            <textarea
              className="ui-input mb-3 min-h-[120px] w-full"
              placeholder="Напишите текст…"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />

            <div className="mb-3 grid gap-3 sm:grid-cols-2">
              <input
                className="ui-input"
                placeholder="Текст кнопки (опционально)"
                value={btnText}
                onChange={(e) => setBtnText(e.target.value)}
              />
              <input
                className="ui-input"
                placeholder="URL кнопки (https://…)"
                value={btnUrl}
                onChange={(e) => setBtnUrl(e.target.value)}
              />
            </div>

            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-neutral-300">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-emerald-500"
                  checked={disablePreview}
                  onChange={(e) => setDisablePreview(e.target.checked)}
                />
                Отключить превью ссылок
              </label>

              <div className="flex items-center gap-2">
                <input
                  className="ui-input w-[160px]"
                  placeholder="test tg_id (опц.)"
                  inputMode="numeric"
                  value={testId}
                  onChange={(e) => setTestId(e.target.value.replace(/[^\d]/g, ""))}
                />
                <span className="text-xs text-neutral-400">если указан — отправим только ему</span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                disabled={!payload || sending}
                onClick={onSend}
                className="rounded-2xl bg-emerald-600/95 px-5 py-3 font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
              >
                {payload?.test_tg_id ? "Отправить тест" : "Отправить"}
              </button>
            </div>
          </>
        )}
      </section>

      <section className="ui-card p-4 sm:p-5">
        <div className="mb-3 text-sm text-neutral-300">История отправок</div>
        {history.length === 0 ? (
          <div className="text-sm text-neutral-400">Пока пусто.</div>
        ) : (
          <ul className="grid gap-2">
            {history.map((h) => (
              <li key={h.id} className="ui-panel p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs text-neutral-400">
                    {new Date(h.created_at).toLocaleString()}
                    {" · "}
                    {h.audience} · {h.criteria}
                    {h.test ? " · TEST" : ""}
                  </div>
                  <div className="text-xs">
                    <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-0.5 text-emerald-200">
                      sent {h.sent}
                    </span>
                    <span className="ml-2 rounded-full border border-red-400/30 bg-red-500/10 px-2 py-0.5 text-red-200">
                      failed {h.failed}
                    </span>
                  </div>
                </div>
                <div className="mt-2 whitespace-pre-wrap text-sm text-white/90">{h.text}</div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
